import { CaretLeft, PlusCircle } from "@phosphor-icons/react";
import { type JSX, useState } from "react";

import CreateWorkspaceModal from "common/components/CreateWorkspaceModal";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import useInvitations from "common/datahooks/useInvitations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";

import InvitationsTable from "./InvitationsTable";
import WorkspacesList from "./WorkspacesList";
import WorkspacesTable from "./WorkspacesTable";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function Workspaces() {
  const isDesktop = useTwBreakpoint("lg");
  const [inputValue, setInputValue] = useState("");
  const { workspaces } = useWorkspaces();
  const { invitations } = useInvitations();
  const { goBackToWorkspace } = useWorkspaceNavigate();

  function clearSearch() {
    setInputValue("");
  }

  const lowercasedInput = inputValue.toLowerCase();

  const filteredWorkspaces = workspaces.filter(({ name }) =>
    name.toLowerCase().includes(lowercasedInput),
  );
  const filteredInvitations = invitations
    ? invitations.filter(({ workspace_name }) =>
        workspace_name.toLowerCase().includes(lowercasedInput),
      )
    : [];

  let mainContent: JSX.Element;

  if (inputValue && !filteredInvitations.length && !filteredWorkspaces.length) {
    mainContent = (
      <EmptyPlaceholder
        title="No results found"
        subtitle="There were no results that match your criteria"
        imageSrc={NoResultsImage}
        actionComponent={<Button onClick={clearSearch}>Clear Search</Button>}
      />
    );
  } else {
    mainContent = isDesktop ? (
      <>
        <RenderIf condition={!!invitations?.length}>
          <InvitationsTable invitations={filteredInvitations} />
        </RenderIf>
        <WorkspacesTable workspaces={filteredWorkspaces} />
      </>
    ) : (
      <WorkspacesList
        workspaces={filteredWorkspaces}
        invitations={filteredInvitations}
      />
    );
  }

  return (
    <div className="flex flex-col gap-y-4 max-w-[808px] mx-auto w-full">
      <div className="lg:mb-6 flex items-center gap-x-1.5">
        <Button
          variant="ghost-black"
          size="sm"
          intent="iconOnly"
          onClick={goBackToWorkspace}
          className="max-lg:hidden"
        >
          <CaretLeft />
        </Button>
        <h1 className="text-lg-bold">Workspaces</h1>
        <Tag variant="secondary" className="lg:hidden">
          {workspaces.length}
        </Tag>
        <CreateWorkspaceModal
          triggerButton={
            <Button
              leftIcon={<PlusCircle weight="fill" />}
              className="ml-auto"
              size={isDesktop ? "sm" : "md"}
            >
              New workspace
            </Button>
          }
        />
      </div>
      <SearchInput
        value={inputValue}
        onChange={(e) => setInputValue(e.currentTarget.value)}
        onClear={clearSearch}
        placeholder="Search workspaces"
        className="lg:max-w-80"
        variant={isDesktop ? "sm" : "lg"}
      />

      {mainContent}
    </div>
  );
}
